/**
 * passport-card-data — the ONE place a consumer-side surface turns a data
 * source into `PassportCardData`, the props shape of `@sill/shared`'s
 * `PassportCard`.
 *
 * Why this module exists (2026-08-13 handoff H1): there was already exactly
 * one passport COMPONENT, but no single function that populated it, so every
 * consumer surface re-derived `PassportCardData` from a different source in a
 * different way — and the onboarding funnel gave up and forked the component
 * outright. The fork rendered "Building your buying-power estimate…" for a
 * buyer whose dashboard card, from the same profile, was complete. One
 * component with N ad-hoc adapters is not unification.
 *
 * Two entry points, one per real source:
 *
 *   - `fromOwnPassport` — the owner-scoped `GET /api/marketplace/passport`
 *     projection. Near-identity: `PassportViewWire` is field-for-field
 *     `PassportCardData`. **No string parsing** — the backend already emits
 *     structured bands here.
 *   - `fromAgentPreview` — the `/api/profile/agent-preview` projection. Needs
 *     string parsing only because that endpoint returns PRE-FORMATTED display
 *     strings ("$650k–$720k", "740+") rather than numeric bands. If that
 *     endpoint ever emits structured bands, the parsing below dies with it.
 *
 * NOT a universal chokepoint — deliberately consumer-side only. The pro app's
 * marketplace wire (`frontend/pro/src/lib/api.ts` `MarketplacePassportCard`)
 * ALREADY is `PassportCardData` and is passed straight through. Routing it
 * through this module would invert the dependency (pro → consumer) to
 * re-derive a shape it already holds. Do not "finish the job" by importing
 * this from `frontend/pro`.
 */
import type {
  PassportCardData,
  PassportLine,
  PassportMoneyBand,
} from "@sill/shared/components/marketplace/passport-card";

import type {
  AgentPreviewProfile,
  PassportPreview,
} from "../hooks/useAgentPreview";
import type { DashboardSnapshot } from "./dashboard";
import { deriveBadgeTier } from "../components/dashboard/profile/tier-derivation";
import type { OwnPassport, PassportViewWire } from "./marketplace-passport-api";

// ──── Own-view (structured wire → card) ───────────────────────────────────────

/**
 * The owner's own passport projection → card props.
 *
 * `PassportViewWire` and `PassportCardData` are the same field set; the only
 * real translation is `buyer_handle` → `display_code`, because the wire names
 * the handle and the card names the rendered line. Everything else is a
 * straight carry, INCLUDING the fields the previous `fromWire()` dropped on
 * the floor (`price_bracket`, `activity_bucket`, `goal`, `timeline`,
 * `target_area`, and every line `band`) — dropping them is what forced the
 * funnel's fork.
 *
 * Nothing is fabricated when a field is absent: an omitted `buying_power`
 * stays omitted and `PassportCard` simply renders no buying-power block,
 * rather than a `$0` or a "pending" shell parked on a terminal screen.
 */
export function fromPassportWire(wire: PassportViewWire): PassportCardData {
  return {
    passport_id: wire.passport_id,
    display_code: wire.buyer_handle ? `Buyer #${wire.buyer_handle}` : undefined,
    tier: wire.tier,
    variant: wire.variant,
    goal: wire.goal,
    timeline: wire.timeline,
    target_area: wire.target_area,
    price_bracket: wire.price_bracket,
    price_kind: wire.price_kind,
    price_approximate: wire.price_approximate,
    activity_bucket: wire.activity_bucket,
    buying_power: wire.buying_power,
    lines: wire.lines ?? [],
  };
}

/**
 * The caller-facing `OwnPassport` (what `getOwnMarketplacePassport` returns and
 * what every own-view surface holds) → card props. The publish/admission
 * decisions alongside it are NOT card data and stay on `OwnPassport`.
 */
export function fromOwnPassport(own: OwnPassport): PassportCardData {
  return fromPassportWire(own.passport);
}

// ──── Agent-preview (formatted strings → card) ────────────────────────────────

function passportLineProvenance(tier: string | null | undefined): string {
  if (tier === "connected" || tier === "pre_approval") return "verified";
  if (tier === "document") return "document";
  return "self_reported";
}

function maybeBand(
  low: number | null | undefined,
  high: number | null | undefined,
  period?: string,
): PassportMoneyBand | null {
  if (low == null || high == null || low < 0 || high < 0) return null;
  return { low, high, period };
}

function parseCompactMoneyToken(raw: string): number | null {
  const match = /^\$?\s*([\d,.]+)\s*([kKmM])?$/.exec(raw.trim());
  if (!match) return null;
  const base = Number(match[1].replace(/,/g, ""));
  if (!Number.isFinite(base)) return null;
  const suffix = match[2]?.toLowerCase();
  if (suffix === "m") return Math.round(base * 1_000_000);
  if (suffix === "k") return Math.round(base * 1_000);
  return Math.round(base);
}

export function parseCompactMoneyBand(
  raw: string | null | undefined,
): PassportMoneyBand | null {
  if (!raw) return null;
  const parts = raw
    .split(/[–-]/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length !== 2) return null;
  const low = parseCompactMoneyToken(parts[0]);
  const high = parseCompactMoneyToken(parts[1]);
  if (low == null || high == null) return null;
  return { low, high };
}

/**
 * Parses the human credit_band display string into a numeric score band.
 * Handles all four backend buckets ("740+", "700–739", "660–699", "Below 660")
 * using the same numeric edges as the backend marketplace projection
 * (passportCreditBand), so the consumer Agent view and the pro marketplace
 * render the identical range.
 */
export function parseCreditBand(
  raw: string | null | undefined,
): PassportMoneyBand | null {
  if (!raw) return null;
  const range = /(\d{3})\D+(\d{3})/.exec(raw);
  if (range)
    return { low: Number(range[1]), high: Number(range[2]), period: "score" };
  const plus = /^(\d{3})\s*\+$/.exec(raw.trim());
  if (plus) return { low: Number(plus[1]), high: 850, period: "score" };
  const below = /^below\s+(\d{3})$/i.exec(raw.trim());
  if (below) return { low: 300, high: Number(below[1]) - 1, period: "score" };
  return null;
}

/**
 * Highest badge-worthy tier (1/2/3, mirrors `deriveBadgeTier`) derived from
 * ONLY the agent-preview `passportPreview` per-field tiers — no
 * `DashboardSnapshot` required. Used by callers (e.g. the relationship
 * profile page) that have an `AgentPreviewProfile` but never fetch the full
 * dashboard snapshot. `deriveBadgeTier(snapshot)` remains the source of
 * truth wherever a snapshot IS already in hand (the Passport page itself) —
 * this is a snapshot-less approximation over the same tier vocabulary
 * ("connected"/"pre_approval" -> bank, "document" -> doc, else self).
 */
export function highestPreviewTier(pp: PassportPreview | null): 1 | 2 | 3 {
  if (!pp) return 1;
  if (pp.overallTier === "t3") return 3;
  if (pp.overallTier === "t2") return 2;
  if (pp.overallTier === "t1") return 1;
  const tiers = [
    pp.householdIncomeTier,
    pp.downPaymentTier,
    pp.monthlyDebtTier,
  ];
  if (
    tiers.filter((t) => t === "connected" || t === "pre_approval").length >= 2
  )
    return 3;
  if (
    tiers.filter(
      (t) => t === "connected" || t === "pre_approval" || t === "document",
    ).length >= 2
  )
    return 2;
  return 1;
}

/**
 * Maps an agent-preview result onto the shared `PassportCardData` shape
 * every marketplace passport surface renders (consumer Agent view, pro
 * marketplace, and — SILL-513 — the consumer relationship profile page).
 *
 * `snapshot` is OPTIONAL: when provided (the Passport page's own call
 * site), the tier badge and a few field fallbacks come from the full
 * `DashboardSnapshot` exactly as before. When omitted (a caller that only
 * has the `agentPreview` result, e.g. `RelationshipProfilePage`), tier
 * falls back to `highestPreviewTier` and the other fallbacks are simply
 * skipped — `agentPreview` itself already carries the fields those
 * fallbacks exist for in the common case.
 */
export function fromAgentPreview(
  agentPreview: AgentPreviewProfile,
  snapshot?: DashboardSnapshot,
): PassportCardData {
  const pp = agentPreview.passportPreview;
  const lines: PassportLine[] = [];
  const pushLine = (
    kind: string,
    band: PassportMoneyBand | null,
    tier: string | null | undefined,
  ) => {
    if (!band) return;
    lines.push({
      kind,
      state: "provided",
      band,
      provenance: passportLineProvenance(tier),
    });
  };

  pushLine(
    "income",
    maybeBand(pp?.householdIncomeLow, pp?.householdIncomeHigh, "annual"),
    pp?.householdIncomeTier,
  );
  pushLine(
    "down_payment",
    maybeBand(pp?.downPaymentLow, pp?.downPaymentHigh),
    pp?.downPaymentTier,
  );
  pushLine(
    "debts",
    maybeBand(pp?.monthlyDebtLow, pp?.monthlyDebtHigh, "monthly"),
    pp?.monthlyDebtTier,
  );
  pushLine(
    "credit",
    parseCreditBand(pp?.creditBand ?? agentPreview.creditScoreRange),
    "self_reported",
  );

  const buyingPower =
    maybeBand(pp?.estimatedQualificationLow, pp?.estimatedQualificationHigh) ??
    maybeBand(
      agentPreview.affordabilityBandLow,
      agentPreview.affordabilityBandHigh,
    );

  const tierNum = pp?.overallTier
    ? highestPreviewTier(pp)
    : snapshot
      ? deriveBadgeTier(snapshot)
      : highestPreviewTier(pp);

  return {
    passport_id: pp?.anonymizedHandle ?? "buyer",
    display_code: `Buyer #${pp?.anonymizedHandle ?? "—"}`,
    tier: `t${tierNum}`,
    variant: "standard",
    goal: agentPreview.buyerType ?? snapshot?.buyerType,
    timeline: agentPreview.timeline ?? snapshot?.timeline,
    // The backend has already reduced a property address to a broad area.
    // Never fall back to the owner's exact target when that field is absent.
    target_area: agentPreview.targetArea ?? undefined,
    price_bracket: parseCompactMoneyBand(agentPreview.targetPriceBand),
    price_kind: agentPreview.priceKind,
    price_approximate: agentPreview.priceApproximate,
    activity_bucket: "active",
    buying_power: buyingPower,
    lines,
  };
}
